import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Table, Button, Icon, Label, Popup } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { signDerivativeForUser } from '../../utils/fetchSignedUrlByFolderV2';
// Spreadsheet view uses the collapsing wrapper so densely-populated Voice
// Variations cells don't push their grid row down on long lists. See
// `VariationBadgeListDropdown.jsx` for the design rationale. Card view and
// the Opportunity Details page intentionally continue to use the plain
// `VariationBadgeList` (more vertical room available there).
import VariationBadgeListDropdown from './VariationBadgeListDropdown';
import MergedNamesHint from './MergedNamesHint';
import VariationStatusLegend from './VariationStatusLegend';
// Inline ⓥ trigger that opens the Character Context Hover Panel — same
// affordance used in OpportunityCard and OpportunityDetails, sharing one
// viewport-docked positioning model across all three surfaces. See:
// docs/journal/PHASE_4D_VA_OPPORTUNITY_CHARACTER_HOVER_PANEL_DESIGN.md §1.1
import CharacterContextTrigger from '../characterContext/CharacterContextTrigger';



/**
 * OpportunitySpreadsheet component displays opportunities in a grouped table format
 * 
 * @param {Array} opportunities - Array of opportunity objects
 * @param {Function} onApply - Function to call when Apply button is clicked
 * @param {Function} onViewDetails - Function to call when View Details button is clicked
 * @param {Function} hasApplied - Function to check if user has applied
 */
const OpportunitySpreadsheet = ({
  opportunities,
  onApply,
  onViewDetails,
  hasApplied
}) => {
  const userId = useSelector(state => state.user.profile?.data?.userId);
  const [bookCovers, setBookCovers] = useState({});

  // Cap on collage thumbnails shown inline before the "+N" overflow popover.
  const COLLAGE_CAP = 6;

  // Group by LitGroup (series) when present, else by the individual book (lone titles). A shared
  // character has exactly one opportunity, so a series bucket lists the whole cast once; the book a
  // character's opportunity happens to point at only feeds the cover collage. See
  // docs/journal/OPPORTUNITY_MARKETPLACE_LITGROUP_DISPLAY_SPEC.md.
  const groupedOpportunities = useMemo(() => {
    const grouped = opportunities.reduce((acc, opp) => {
      const isSeries = !!opp.litGroup?.uuid;
      const key = isSeries ? `lg:${opp.litGroup.uuid}` : `bk:${opp.book?.uuid}`;
      if (!acc[key]) {
        acc[key] = {
          key,
          type: isSeries ? 'series' : 'book',
          title: isSeries ? (opp.litGroup.name || 'Series') : (opp.book?.name || 'Book'),
          // Series → the FULL book set the server attaches (page-independent, publication order).
          // Lone book → the single book. Normalized to { uuid, name, bookCover }.
          booksList: isSeries
            ? (opp.litGroup.books || []).map(b => ({ uuid: b.uuid, name: b.name, bookCover: b.bookCover }))
            : (opp.book?.uuid
                ? [{ uuid: opp.book.uuid, name: opp.book.name, bookCover: opp.book.profile?.bookCover }]
                : []),
          author: isSeries ? null : (opp.book?.profile?.author || null),
          opportunities: [],
        };
      }
      acc[key].opportunities.push(opp);
      return acc;
    }, {});

    // NO client-side re-sort.
    //
    // This used to sort characters alphabetically within each group and groups by title. That was
    // correct when the browser held the entire result set — and became actively wrong the moment
    // the server started sorting and paginating. Re-sorting a page of ten silently overrode the
    // actor's chosen order: picking "Most dialogue lines" would fetch the right ten rows and then
    // shuffle them back into alphabetical order, so the control looked broken. Order is the
    // server's job now, because only the server can see all 1,512 rows.
    return Object.values(grouped);
  }, [opportunities]);

  // Series descriptor for a given opportunity, keyed the same way the grouping is. Rows are flat
  // now (see the Table.Body note), so each row looks its own series up rather than inheriting it
  // from a rowSpan'd cell above it.
  const seriesFor = useMemo(() => {
    const byKey = {};
    groupedOpportunities.forEach(g => { byKey[g.key] = g; });
    return (opp) => byKey[opp.litGroup?.uuid ? `lg:${opp.litGroup.uuid}` : `bk:${opp.book?.uuid}`];
  }, [groupedOpportunities]);

  // Casting tier drives both the badge colour and the ordering the server applies. Kept in one
  // place so the two cannot drift apart.
  const TIER_DISPLAY = {
    narration: { label: 'Narrator', color: 'violet' },
    lead: { label: 'Lead', color: 'red' },
    support: { label: 'Support', color: 'orange' },
    walkon: { label: 'Walk-on', color: 'grey' },
    nonspeaking: { label: 'Non-speaking', color: 'black' }
  };

  // Sign covers for every distinct book across all groups — the collage needs each series book's
  // cover, not just one.
  /**
   * ⭐ TWO FIXES HERE, 2026-10-22.
   *
   * 1. **The `micro` derivative instead of the original.** These render at 34×51 px and were
   *    fetching ~2,839 KB each; `micro` is 3,072 bytes. ⓘ `signDerivativeForUser` falls back to
   *    the original when no derivative exists, so an un-backfilled image is slow, not broken.
   * 2. ⛔ **The loop was SERIAL** — `await` inside a `for`, so twenty-five books meant twenty-five
   *    sequential signing round-trips, each waiting for the one before. Collect the work first,
   *    then `Promise.all`. ⓘ `signedUrlCache` already de-duplicates repeats within the hour, so
   *    asking for all of them at once costs one round-trip of latency rather than N.
   */
  useEffect(() => {
    const loadBookCovers = async () => {
      const wanted = new Map();
      for (const group of groupedOpportunities) {
        for (const book of group.booksList) {
          if (book?.bookCover && userId && !wanted.has(book.uuid)) {
            wanted.set(book.uuid, book.bookCover);
          }
        }
      }
      const entries = await Promise.all([...wanted.entries()].map(async ([uuid, original]) => {
        try {
          const { url } = await signDerivativeForUser(userId, original, 'micro');
          return [uuid, url];
        } catch (error) {
          console.error(`Failed to load cover for book ${uuid}:`, error);
          return [uuid, null];
        }
      }));
      setBookCovers(Object.fromEntries(entries.filter(([, url]) => url)));
    };

    if (groupedOpportunities.length > 0 && userId) {
      loadBookCovers();
    }
  }, [opportunities, userId]); // Depend on opportunities array directly

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Open':
        return 'green';
      case 'Assigned':
        return 'blue';
      case 'Completed':
        return 'purple';
      case 'Cancelled':
        return 'red';
      default:
        return 'grey';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'High':
        return 'red';
      case 'Medium':
        return 'yellow';
      case 'Low':
        return 'blue';
      default:
        return 'grey';
    }
  };

  // Format deadline
  const formatDeadline = (deadline) => {
    return deadline ? format(new Date(deadline), 'MMM d, yyyy') : 'No deadline';
  };

  if (opportunities.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <p>No opportunities to display</p>
      </div>
    );
  }

  return (
    <div style={{ overflowX: 'auto' }}>
      <Table celled structured unstackable>
        <Table.Header style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10 }}>
          <Table.Row>
            <Table.HeaderCell width={3}>Series</Table.HeaderCell>
            <Table.HeaderCell width={3}>Role</Table.HeaderCell>
            <Table.HeaderCell width={3}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                Voice Variations
                {/* Accessible legend — same one used in ManageOpportunities
                    and OpportunityDetails so the badge colors/icons stay
                    consistently discoverable across the marketplace. */}
                <VariationStatusLegend compact />
              </span>
            </Table.HeaderCell>

            {/* <Table.HeaderCell width={4}>Description</Table.HeaderCell> */}
            <Table.HeaderCell width={1}>Status</Table.HeaderCell>
            <Table.HeaderCell width={1}>Priority</Table.HeaderCell>
            {/* "Comp." rather than "Compensation": the header was the widest thing in the column
              * and was holding it open far past its contents, so the width went to Series
              * instead, where the cover art lives. */}
            <Table.HeaderCell width={1}>Comp.</Table.HeaderCell>
            <Table.HeaderCell width={2}>Deadline</Table.HeaderCell>
            <Table.HeaderCell width={1}>Actions</Table.HeaderCell>
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {/* FLAT ROWS.
            *
            * These used to be nested: an outer map over series groups, an inner map over that
            * group's opportunities, and a rowSpan'd series cell emitted only on the first row.
            * That construct cannot survive pagination - a group split across a page boundary
            * emits a rowSpan taller than the rows left on the page, and under the default tier
            * sort a page of ten interleaves ten different series, so every row becomes its own
            * one-row "group" carrying a full cover collage.
            *
            * The series is now an ATTRIBUTE OF THE ROW, which is also the truer model: a voice
            * actor is shopping for a ROLE, and the series says which world it belongs to. The
            * display spec's "this cast serves all these books" cue survives as the inline
            * collage, per row, where the actor is actually looking. */}
          {opportunities.map((opportunity, rowIndex) => {
            const group = seriesFor(opportunity);
            const books = group?.booksList || [];

            // Repeat the series block only when the series CHANGES from the row above.
            //
            // This is what brings back the grouped look the rowSpan used to give, without the
            // rowSpan — which could not cross a page boundary. It also self-adjusts to the sort:
            // under "Series, then character" consecutive rows share a series and the block appears
            // once per group, while under a tier sort neighbours rarely match and every row labels
            // itself. Deliberately keyed off the PREVIOUS ROW rather than a group index, so the
            // first row of every page always carries its series no matter where the page was cut.
            const prev = rowIndex > 0 ? opportunities[rowIndex - 1] : null;
            const seriesKeyOf = (o) => o?.litGroup?.uuid || o?.book?.uuid || null;
            const repeatsSeries = prev !== null && seriesKeyOf(prev) === seriesKeyOf(opportunity);
            const tier = TIER_DISPLAY[opportunity.character?.castingTier] || null;
            const lineCount = opportunity.character?.dialogueLineCount;
            const profile = [
              opportunity.character?.suggestedGender,
              opportunity.character?.suggestedAge,
              opportunity.character?.suggestedArchetype
            ].filter(Boolean).join(' · ');

            return (
              <Table.Row key={opportunity.uuid}>

                {/* ---------- Series ---------- */}
                <Table.Cell verticalAlign='top' style={repeatsSeries ? { borderTop: 'none' } : undefined}>
                  {repeatsSeries ? null : (<>
                  {/* Cover art and title sit side by side rather than stacked. Stacking forced the
                    * covers small to keep the row short; running them horizontally lets the art be
                    * legible at 34px and uses the width reclaimed from the Compensation header. */}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px', flexShrink: 0, maxWidth: '80px' }}>
                    {books.slice(0, COLLAGE_CAP).map((b) => (
                      bookCovers[b.uuid] ? (
                        <img key={b.uuid} src={bookCovers[b.uuid]} alt={b.name} title={b.name}
                          style={{ width: '34px', height: '51px', objectFit: 'cover', borderRadius: '3px' }} />
                      ) : (
                        <div key={b.uuid} title={b.name}
                          style={{ width: '34px', height: '51px', background: '#eee', borderRadius: '3px' }} />
                      )
                    ))}
                    {books.length > COLLAGE_CAP && (
                      <Popup
                        on="hover"
                        position="right center"
                        trigger={
                          <div style={{ width: '34px', height: '51px', background: '#f5f5f5', borderRadius: '3px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', color: '#666', cursor: 'default' }}>
                            +{books.length - COLLAGE_CAP}
                          </div>
                        }
                        content={
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxWidth: '280px' }}>
                            {books.map((b) => (
                              bookCovers[b.uuid] ? (
                                <img key={b.uuid} src={bookCovers[b.uuid]} alt={b.name} title={b.name}
                                  style={{ width: '48px', height: '72px', objectFit: 'cover', borderRadius: '3px' }} />
                              ) : (
                                <div key={b.uuid} title={b.name}
                                  style={{ width: '48px', height: '72px', background: '#eee', borderRadius: '3px' }} />
                              )
                            ))}
                          </div>
                        }
                      />
                    )}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: '1rem' }}>
                      {group?.title || 'Unknown'}
                    </div>
                    <div style={{ fontSize: '0.72rem', color: '#888', lineHeight: '0.9rem', marginTop: '2px' }}>
                      {group?.type === 'series'
                        ? `${books.length} book${books.length === 1 ? '' : 's'} · series`
                        : (group?.author ? `by ${group.author}` : 'single title')}
                    </div>
                  </div>
                  </div>
                  </>)}
                </Table.Cell>

                {/* ---------- Role ----------
                  * Character name, casting tier and dialogue-line count together. Tier and count
                  * already existed on the Character node (stamped by casting derivation) but had
                  * never been surfaced here, so the marketplace could not answer the first
                  * question an actor asks: how big is this part? Measured 2026-08-31, 879 of the
                  * 1,512 open roles are walk-ons of four lines or fewer - without this the 108
                  * leads were indistinguishable from them.
                  *
                  * The em-dash badge is deliberate: a series whose casting has not been derived
                  * yet should read as "not known", not as a missing or broken value. */}
                <Table.Cell style={{ position: 'relative' }} verticalAlign='top'>
                  <strong>{opportunity.character?.name || 'No character'}</strong>
                  <span style={{ position: 'absolute', top: 4, right: 6 }}>
                    <MergedNamesHint aliases={opportunity.character?.aliases} />
                  </span>
                  <div style={{ marginTop: '4px' }}>
                    {tier ? (
                      <Label size='mini' color={tier.color} style={{ marginRight: '5px' }}>
                        {tier.label}
                      </Label>
                    ) : (
                      <Label size='mini' basic style={{ marginRight: '5px' }}
                        title='Casting has not been derived for this series yet'>
                        &mdash;
                      </Label>
                    )}
                    <span style={{ fontSize: '0.75rem', color: '#666' }}>
                      {lineCount != null
                        ? `${lineCount} line${lineCount === 1 ? '' : 's'}`
                        : 'line count pending'}
                    </span>
                  </div>
                  {profile && (
                    <div style={{ fontSize: '0.72rem', color: '#999', marginTop: '2px' }}>
                      {profile}
                    </div>
                  )}
                </Table.Cell>



                  <Table.Cell>
                    <VariationBadgeListDropdown
                      variations={opportunity.variations}
                      columns={2}
                      maxRows={2}
                    />
                  </Table.Cell>

                  {/* <Table.Cell>
                    {opportunity.description.length > 100
                      ? `${opportunity.description.substring(0, 100)}...`
                      : opportunity.description}
                  </Table.Cell> */}

                  <Table.Cell textAlign='center'>
                    <Label
                      color={getStatusColor(opportunity.status)}
                      size='tiny'
                    >
                      {opportunity.status}
                    </Label>
                  </Table.Cell>

                  <Table.Cell textAlign='center'>
                    {opportunity.priority && (
                      <Label
                        color={getPriorityColor(opportunity.priority)}
                        size='tiny'
                      >
                        {opportunity.priority}
                      </Label>
                    )}
                  </Table.Cell>

                  <Table.Cell>
                    {opportunity.compensation && (
                      <div>
                        <div>{opportunity.compensation}</div>
                        {opportunity.compensationType && (
                          <div style={{ fontSize: '0.85em', color: '#666' }}>
                            ({opportunity.compensationType})
                          </div>
                        )}
                      </div>
                    )}
                  </Table.Cell>

                  <Table.Cell>
                    <Icon name='calendar' />
                    {formatDeadline(opportunity.deadline)}
                  </Table.Cell>

                  <Table.Cell>
                    <div style={{ display: 'flex', gap: '5px', flexDirection: 'row', justifyContent: 'center' }}>
                      <Popup
                        content="View Details - See full opportunity information"
                        position='top center'
                        trigger={
                          <Button
                            primary
                            size='tiny'
                            compact
                            icon
                            onClick={() => onViewDetails(opportunity.uuid)}
                          >
                            <Icon name='eye' />
                          </Button>
                        }
                      />

                      {opportunity.status === 'Open' && (
                        hasApplied(opportunity.uuid) ? (
                          <Popup
                            content="Applied - You have already applied for this opportunity"
                            position='top center'
                            trigger={
                              <Button
                                color='green'
                                size='tiny'
                                compact
                                icon
                                disabled
                              >
                                <Icon name='check' />
                              </Button>
                            }
                          />
                        ) : (
                          <Popup
                            content="Apply Now - Submit your application for this opportunity"
                            position='top center'
                            trigger={
                              <Button
                                color='green'
                                size='tiny'
                                compact
                                icon
                                onClick={() => onApply(opportunity.uuid)}
                              >
                                <Icon name='paper plane' />
                              </Button>
                            }
                          />
                        )
                      )}

                      {/* Character Context Hover Panel trigger — rendered
                       * here as the Semantic UI actionButton variant so it
                       * reads as a peer of View Details + Apply Now rather
                       * than as a stray inline glyph. Same component,
                       * different chrome. Placed last in the row so the
                       * action sequence reads view → apply → voice. */}
                      {opportunity.character?.name && (
                        <CharacterContextTrigger
                          variant="actionButton"
                          bookUuid={opportunity.book?.uuid}
                          characterId={opportunity.character?.uuid}
                          characterName={opportunity.character.name}
                          bookTitle={opportunity.book?.name}
                        />
                      )}
                    </div>
                  </Table.Cell>

              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </div>
  );
};

OpportunitySpreadsheet.propTypes = {
  opportunities: PropTypes.arrayOf(PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    priority: PropTypes.string,
    deadline: PropTypes.string,
    compensation: PropTypes.string,
    compensationType: PropTypes.string,
    book: PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      profile: PropTypes.shape({
        author: PropTypes.string,
        bookCover: PropTypes.string
      })
    }).isRequired,
    // Present when the book belongs to a LitGroup — drives series grouping + the cover collage.
    litGroup: PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string
    }),
    character: PropTypes.shape({
      uuid: PropTypes.string,
      name: PropTypes.string
    })
  })).isRequired,
  onApply: PropTypes.func.isRequired,
  onViewDetails: PropTypes.func.isRequired,
  hasApplied: PropTypes.func.isRequired
};

export default OpportunitySpreadsheet;
